// The 6-segment stage progress bar: proven stages filled, verbal-only stages hatched.

import type { StageStep } from '../src/api/contract';
import { h } from './dom';
import { STAGES, STAGE_ACTS, isStage, stageNamesFor } from './format';

export interface StageProgress {
  /** Stages proven by a tool call or a canary leak (B can skip stages). */
  proven: Set<number>;
  highestStage: number;
  highestVerbalStage: number;
}

export function progressOf(m: {
  highestStage?: number | null;
  highestVerbalStage?: number | null;
  stageSequence?: StageStep[] | null;
}): StageProgress {
  const proven = new Set<number>();
  const seq = Array.isArray(m.stageSequence) ? m.stageSequence : [];
  for (const step of seq) if (step && isStage(step.stage)) proven.add(step.stage);
  const highest = clampStage(m.highestStage);
  // Older or partial rows may lack the sequence: fall back to "everything up to the high-water mark".
  if (proven.size === 0) for (let s = 1; s <= highest; s++) proven.add(s);
  return { proven, highestStage: Math.max(highest, ...proven, 0), highestVerbalStage: clampStage(m.highestVerbalStage) };
}

function clampStage(n: number | null | undefined): number {
  return typeof n === 'number' && Number.isFinite(n) ? Math.min(6, Math.max(0, Math.round(n))) : 0;
}

type SegState = 'proven' | 'verbal' | 'none';

function segState(stage: number, p: StageProgress): SegState {
  if (p.proven.has(stage)) return 'proven';
  if (stage > p.highestStage && stage <= p.highestVerbalStage) return 'verbal';
  return 'none';
}

export function describeProgress(p: StageProgress): string {
  const proven = [...p.proven].sort((a, b) => a - b);
  const parts: string[] = [];
  parts.push(proven.length ? `Highest stage proven: ${p.highestStage} of 6 (proven: ${proven.join(', ')})` : 'No stage proven');
  if (p.highestVerbalStage > p.highestStage) parts.push(`verbal agreement up to stage ${p.highestVerbalStage}`);
  return `${parts.join('; ')}.`;
}

export class StageBar {
  readonly el: HTMLElement;
  private readonly segs: HTMLElement[] = [];
  private progress: StageProgress = { proven: new Set(), highestStage: 0, highestVerbalStage: 0 };

  /** The match's setup, which names its stages. */
  private readonly setup: string | null;

  constructor(opts: { compact?: boolean; setup?: string | null } = {}) {
    this.setup = opts.setup ?? null;
    this.el = h('div', { class: `stagebar${opts.compact ? ' stagebar--compact' : ''}`, attrs: { role: 'img' } });
    for (const stage of STAGES) {
      const seg = h('span', { class: 'seg seg--none', attrs: { 'data-stage': stage } }, opts.compact ? null : String(stage));
      this.segs.push(seg);
      this.el.append(seg);
    }
    this.render();
  }

  get value(): StageProgress {
    return this.progress;
  }

  update(p: StageProgress): void {
    this.progress = { proven: new Set(p.proven), highestStage: p.highestStage, highestVerbalStage: p.highestVerbalStage };
    this.render();
  }

  /** Brief highlight when a stage lands live (CSS skips the animation under reduced motion). */
  flash(stage: number): void {
    const seg = this.segs[stage - 1];
    if (!seg) return;
    seg.classList.remove('seg--flash');
    void seg.offsetWidth; // restart the animation
    seg.classList.add('seg--flash');
    window.setTimeout(() => seg.classList.remove('seg--flash'), 1600);
  }

  private render(): void {
    this.el.setAttribute('aria-label', describeProgress(this.progress));
    this.segs.forEach((seg, i) => {
      const stage = i + 1;
      const state = segState(stage, this.progress);
      seg.classList.remove('seg--proven', 'seg--verbal', 'seg--none');
      seg.classList.add(`seg--${state}`);
      const name = isStage(stage) ? stageNamesFor(this.setup)[stage] : '';
      const what = state === 'proven' ? 'proven' : state === 'verbal' ? 'agreed verbally only' : 'not reached';
      seg.title = `Stage ${stage} · ${name}: ${what}`;
    });
  }
}

export function stageBar(p: StageProgress, compact = false, setup?: string | null): HTMLElement {
  const bar = new StageBar({ compact, setup });
  bar.update(p);
  return bar.el;
}

export function stageLegend(): HTMLElement {
  const item = (cls: string, text: string): HTMLElement =>
    h('li', null, h('span', { class: `legend-swatch seg ${cls}`, attrs: { 'aria-hidden': 'true' } }), text);
  return h(
    'ul',
    { class: 'legend' },
    item('seg--proven', 'Proven (tool call or leak)'),
    item('seg--verbal', 'Verbal only (both judges agreed)'),
    item('seg--none', 'Not reached'),
  );
}

/** The six stages as a numbered ladder: the one idea every score on the site counts. */
export function stageLadder(): HTMLElement {
  return h(
    'ol',
    { class: 'ladder' },
    ...STAGES.map((s) => h('li', null, h('span', { class: 'ladder-n' }, String(s)), h('span', { class: 'ladder-act' }, STAGE_ACTS[s]))),
  );
}
